// sendDataToSentry.js
'use strict';

import getMeData from '../selector/getMeData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import {
  getSentry2Scope,
  replaceStringsToAvoidDataScrubbing,
} from '../resource/sentry.js';
import splitTextByByteLength from '../resource/splitTextByByteLength.js';
import { SENTRY_BYTES_LIMIT_FOR_EXTRA_DATA } from '../RemoteConfigKeys.js';

/**
 * Data processing:
 * 1. Serialize data object to data string.
 * 2. Replace some sensitive words for both key and value to avoid data scrubbing by Sentry.
 * 3. See if splitting is needed (It will triggered when data is over a specified number).
 * 4. Set processed key and value as additional data for Sentry.
 * @param {object} {sentry2Scope} - Sentry's scope
 * @param {string} {key} - The key of extra data
 * @param {object} {value} - The value of extra data
 * @param {string} {bytesLimit} - The size limitation for an individual extra data
 */
const processDataAndSetExtra = ({ sentry2Scope, key, value, bytesLimit }) => {
  const dataString = JSON.stringify(value ?? '');
  const replacedKey = replaceStringsToAvoidDataScrubbing(key);
  const replacedData = replaceStringsToAvoidDataScrubbing(dataString);
  const dataChunks = splitTextByByteLength(replacedData, bytesLimit);
  if (dataChunks.length === 1) {
    sentry2Scope.setExtras({ [replacedKey]: dataChunks[0] });
  } else if (dataChunks.length > 1) {
    dataChunks.forEach((chunk, i) => {
      sentry2Scope.setExtras({
        [`${replacedKey} - part${i + 1}`]: chunk,
      });
    });
  }
};

/**
 * Send (partial) current remote config
 * @kind action
 * @return {Promise} Action promise.
 */
const sendDataToSentry = () => async (dispatch, getState) => {
  const userName = getMeData(getState(), 'username');
  const bytesLimit = getRemoteConfigData(
    getState(),
    SENTRY_BYTES_LIMIT_FOR_EXTRA_DATA
  );
  const sentry2Scope = getSentry2Scope();
  sentry2Scope.setTag('bug_report', 'settings');
  sentry2Scope.setFingerprint([
    'bug_report',
    'settings',
    userName,
    Date.now(), // so that each report will send to sentry
  ]);

  const dataInReduxStore = getState().toJS();

  processDataAndSetExtra({
    sentry2Scope,
    key: 'me.ab',
    value: dataInReduxStore?.me?.ab,
    bytesLimit,
  });

  const pusherChannels = Object.create(null);
  for (const [channelName, channelData] of Object.entries(
    dataInReduxStore?.operations?.pusher?.channels
  )) {
    if (
      channelName.startsWith('presence-enc-client') ||
      channelName.startsWith('presence-enc-user')
    ) {
      pusherChannels[channelName] = channelData;
    }
  }
  processDataAndSetExtra({
    sentry2Scope,
    key: 'operations.pusher.channels',
    value: pusherChannels,
    bytesLimit,
  });

  // Flatten `operations.config` to individual extra data
  // to enhance readability and avoid division as much as possible.
  for (const [key, value] of Object.entries(
    dataInReduxStore?.operations?.config
  )) {
    processDataAndSetExtra({
      sentry2Scope,
      key: `operations.config.${key}`,
      value,
      bytesLimit,
    });
  }

  processDataAndSetExtra({
    sentry2Scope,
    key: 'remoteConfig',
    value: dataInReduxStore?.remoteConfig,
    bytesLimit,
  });

  sentry2Scope.captureMessage(`[Data Snapshot] ${userName}`);
  return dispatch({ type: '' });
};

export default sendDataToSentry;
